import { brandSchema, IBrand, IMarket, marketSchema } from 'data/schema';
// import { userSession } from 'state/session';
import { isDebugMode, testDataRequested } from 'utils/ui/debug';
import { getStorageItem, setStorageItem } from 'utils/ui/storage';

export interface IUserInfo {
  type: 'dealer' | 'customer';
  brand: IBrand;
  market: {
    iso2: string;
    iso3: IMarket;
  };
  groups: string[];
  email: string;
  name: string;
  phoneNumber: string;
  publicPicture: string;
  dealerIdKvps: string;
  userPseudonym: string;
  dealerIdsList: string[];
}

export interface IUserConfig {
  getAccessTokenCallback: null | (() => string);
  getIdTokenCallback: null | (() => string);
  getUserInfoCallback: null | ((idToken?: string) => IUserInfo | null | undefined);
}

export class User {
  private static readonly DEALER_ID_PATTERN = /^[A-Za-z]{3}[0-9]{5}[A-Za-z]+$/;
  private static readonly TEST_DEALER_IDS = ['DEU00100V', 'DEU00100L'] as const;

  static config: IUserConfig = {
    getAccessTokenCallback: null,
    getIdTokenCallback: null,
    getUserInfoCallback: null,
  };

  getAccessToken() {
    return User.config.getAccessTokenCallback?.();
  }

  getIdToken() {
    return User.config.getIdTokenCallback?.();
  }

  getUserInfo() {
    const idToken = User.config.getIdTokenCallback?.();
    const userInfo = User.config.getUserInfoCallback?.(idToken);

    return userInfo;
  }

  get isDealer() {
    return this.type === 'dealer';
  }

  get type() {
    return this.getUserInfo()?.type ?? 'customer';
  }
  get brand() {
    return getStorageItem('sessionStorage', 'auth:brand') || this.getUserInfo()?.brand || 'A';
  }
  get market() {
    const storedMarket = getStorageItem('sessionStorage', 'auth:market');
    if (storedMarket && storedMarket.length >= 2) {
      return { iso2: storedMarket.slice(0, 2).toLowerCase(), iso3: storedMarket as IMarket };
    }
    return this.getUserInfo()?.market ?? { iso2: 'de', iso3: 'DEU' };
  }
  get groups() {
    return this.getUserInfo()?.groups ?? [];
  }
  get email() {
    return this.getUserInfo()?.email ?? '';
  }
  get name() {
    return this.getUserInfo()?.name ?? '';
  }
  get phoneNumber() {
    return this.getUserInfo()?.phoneNumber ?? '';
  }
  get publicPicture() {
    return this.getUserInfo()?.publicPicture ?? '';
  }

  // The first/primary dealer ID from the list of dealer IDs.
  get dealerIdKvps() {
    return this.getUserInfo()?.dealerIdKvps ?? '';
  }
  get userPseudonym() {
    return this.getUserInfo()?.userPseudonym ?? '';
  }

  // All dealer IDs for this user
  get dealerIdsList() {
    const userDealerIds = this.getUserInfo()?.dealerIdsList ?? [];
    const testDealerIds = this.getTestDealerIds();

    // Combine and deduplicate dealer IDs
    const allDealerIds = [...new Set([...userDealerIds, ...testDealerIds])];

    // then filter for valid format
    return allDealerIds.filter(this.isValidDealerIdFormat);
  }

  private getTestDealerIds(): readonly string[] {
    return testDataRequested() ? User.TEST_DEALER_IDS : [];
  }

  private isValidDealerIdFormat(id: string): boolean {
    return User.DEALER_ID_PATTERN.test(id);
  }

  // This is the selected dealer ID. Some users have multiple and this one holds the most recently selected
  get dealerId(): string {
    return getStorageItem('sessionStorage', 'auth:dealerId') || this.dealerIdKvps;
  }

  set dealerId(dealerId: string) {
    // Extract brand (last letter) and market (first three letters) from dealer ID
    const brand = brandSchema.parse(dealerId.slice(-1));
    const market = marketSchema.parse(dealerId.slice(0, 3));

    setStorageItem('sessionStorage', 'auth:dealerId', dealerId);

    // A change in any one of these will trigger re-routing in standalone
    // Since the session state imports the user module, we are avoiding circular dependency by the means of async import.
    import('state/session').then(({ userSession, getNewSessionData }) => {
      const newSession = getNewSessionData();
      userSession().setValues(
        {
          ...newSession,
          brand,
          market,
        },
        { reset: true }
      );
    });
  }
}

export const user = new User();

if (isDebugMode()) {
  // @ts-expect-error - expose config in global scope for debugging
  window.user = user;
}
